<script>
import Layout from '@layouts/iframeLayout.vue'
import AwardWinner from '@components/awards/award-winner.vue'
import LoaderIcon from '@components/loader-icon.vue'
import _ from 'lodash'
import { getAllAwards, getAward } from '@src/api/awards'
import { transformMany as transformAwards, transformAwardWinners } from '@src/api/transformers/awardTransformer'

export default {
  page: {
    title: 'Awards',
    meta: [
      {
        name: 'description',
        content: 'The Awards page.',
      },
    ],
  },
  components: {
    Layout,
    AwardWinner,
    LoaderIcon,
  },
  data() {
    return {
      loading: true,
      error: false,
      loadingAwardWinners: true,
      year: '',
      yearOptions: [],
      selectedAwardId: '',
      allAwards: [],
      awardOptions: [],
      awardType: 'User',
      awardWinners: [],
      isAwardSelectorDirty: false,
    }
  },

  computed: {
    getYearOptions() {
      return [...this.yearOptions].sort()
    },
    getAwardOptions() {
      return _.sortBy([...this.awardOptions], 'title')
    },
    getAllAwardsByCurrentAwardType() {
      return _.filter(this.allAwards, { type: this.awardType })
    },
    getSelectedAward() {
      return _.head(_.filter(this.allAwards, { id: this.selectedAwardId }))
    },
  },

  created() {
    this.loadAwards()
  },

  methods: {
    async loadAwards() {
      try {
        const {
          data: { data: awards },
        } = await getAllAwards()
        this.allAwards = transformAwards(awards)
        const { awardId: routeAwardId } = this.$route.query
        if (routeAwardId) {
          this.selectSpecificAward(routeAwardId)
          return
        }

        this.loadYearOptions()
        this.loadAwardOptions()
        this.pickDefaultAward()
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    selectSpecificAward(awardId) {
      this.selectedAwardId = awardId
      const award = this.getSelectedAward
      this.awardType = award.type
      this.loadYearOptions()
      this.loadAwardOptions()
      this.year = award.year
      this.loadAwardWinners()
    },

    loadYearOptions({ selectYear = '' } = {}) {
      const yearOptions = _.map(this.getAllAwardsByCurrentAwardType, 'year')
      this.yearOptions = _.uniq(yearOptions)
      this.year = _.max(this.yearOptions)
      if (selectYear && this.yearOptions.includes(selectYear)) {
        this.year = selectYear
      }
    },

    pickDefaultAward() {
      let lastSelectedAward = null

      if (this.isAwardSelectorDirty) {
        const lastSelectedAwardTitle = _.find(this.allAwards, { id: this.selectedAwardId })?.title
        lastSelectedAward = _.find(this.awardOptions, { title: lastSelectedAwardTitle })
      }

      const defaultAward = lastSelectedAward || _.find(this.awardOptions, 'isDefaultAward') || _.head(this.awardOptions)

      this.selectedAwardId = _.toString(defaultAward.id)
      this.loadAwardWinners()
    },

    pickYear() {
      this.loadAwardOptions()
      this.pickDefaultAward()
    },

    pickAward() {
      this.isAwardSelectorDirty = true
      this.loadAwardWinners()
    },

    pickAwardMobileSelector({ id }) {
      this.isAwardSelectorDirty = true
      this.selectedAwardId = id
      this.loadAwardWinners()
    },

    pickAwardType(type) {
      this.awardType = type
      this.loadYearOptions({ selectYear: this.year })
      this.loadAwardOptions()
      this.pickDefaultAward()
    },

    loadAwardOptions() {
      const awardsByYear = _.chain(this.getAllAwardsByCurrentAwardType).filter({ year: this.year }).value()
      this.awardOptions = _.uniq(awardsByYear)
    },

    clearAwardWinners() {
      this.awardWinners = []
    },

    async loadAwardWinners() {
      try {
        this.clearAwardWinners()
        this.loadingAwardWinners = true
        const { data: award } = await getAward({ id: this.selectedAwardId })
        this.awardWinners = transformAwardWinners(award, this.awardType)
      } catch (err) {
        this.error = true
      } finally {
        this.loadingAwardWinners = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon v-if="loading || error" :error="error"></LoaderIcon>
    <div v-else>
      <div :class="$style.viewContainer">
        <div :class="$style.awardWinnersContainer">
          <div :class="$style.titleHeaderContainer">
            <BaseTitle class="color-primary-blue" :class="$style.title">Awards</BaseTitle>
            <BaseSelect
              v-model="year"
              :class="$style.yearSelect"
              name="Date"
              :options="getYearOptions"
              data-awards-year-select
              @update="pickYear" />
            <BaseSelect
              v-model="selectedAwardId"
              :class="$style.awardSelect"
              name="Award"
              :options="getAwardOptions"
              data-awards-options-select
              option-text="title"
              option-value="id"
              @update="pickAward" />
            <div :class="$style.headerTypeSelector" class="d-flex justify-content-around">
              <BaseSelector
                :active="awardType === 'User'"
                data-award-select-individual
                class="mr-15"
                @click="pickAwardType('User')">
                Individual
              </BaseSelector>
              <BaseSelector :active="awardType === 'Office'" data-award-select-office @click="pickAwardType('Office')">
                Office
              </BaseSelector>
            </div>
          </div>
          <div :class="$style.titleHeaderContainerMobile">
            <BaseTitle class="color-primary-charcoal" :class="$style.title">Awards</BaseTitle>
            <div class="mb-4 d-flex">
              <BaseSelector :active="awardType === 'User'" class="mr-15" @click="pickAwardType('User')">
                Individual
              </BaseSelector>
              <BaseSelector :active="awardType === 'Office'" @click="pickAwardType('Office')">Office</BaseSelector>
            </div>
            <div class="d-flex justify-content-between">
              <BaseSelect
                v-model="year"
                :class="$style.yearSelect"
                name="Date"
                :options="getYearOptions"
                @update="pickYear" />
            </div>
            <div :class="$style.mobileAwardSelector">
              <BaseSelector
                v-for="award in getAwardOptions"
                :key="award.id"
                :active="award.id === selectedAwardId"
                @click="pickAwardMobileSelector(award)">
                {{ award.title }}
              </BaseSelector>
            </div>
          </div>
          <div :class="$style.awardDescriptionContainer">
            <div class="d-flex align-items-center mb-15">
              <img :class="$style.awardBadge" :src="getSelectedAward.imageUrl" alt="" />
              <div>
                <h2 class="color-primary-blue m-10" data-award-title>{{ getSelectedAward.title }}</h2>
                <p class="p1">{{ getSelectedAward.description }}</p>
              </div>
            </div>
          </div>
          <div :class="$style.awardDescriptionContainerMobile">
            <img :class="$style.awardBadge" :src="getSelectedAward.imageUrl" alt="" />
            <p class="p2">{{ getSelectedAward.description }}</p>
          </div>
          <LoaderIcon v-if="loadingAwardWinners" class="mt-5"></LoaderIcon>
          <div v-if="!loadingAwardWinners" :class="$style.awardWinners">
            <AwardWinner
              v-for="awardWinner in awardWinners"
              :key="awardWinner.id"
              :award-winner="awardWinner"
              :is-club-award="getSelectedAward.isClubAward"
              :is-rank-award="getSelectedAward.isRankAward"></AwardWinner>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.viewContainer {
  display: block;
  width: auto;
}

.awardWinnersContainer {
  @media (max-width: $lg-breakpoint) {
    flex: 1 0 auto;
  }

  flex: 1 0 740px;
}

.headerTypeSelector {
  height: fit-content;
  margin-left: 51px;
}

.titleHeaderContainer {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  display: flex;
}

.title {
  @media (max-width: $lg-breakpoint) {
    margin-bottom: 24px;
  }

  margin-right: 40px;
}

.titleHeaderContainerMobile {
  @media (max-width: $lg-breakpoint) {
    display: block;
  }

  display: none;
}

.yearSelect {
  @media (max-width: $lg-breakpoint) {
    width: 127px;
    min-width: 96px;
  }

  min-width: 154px;
  margin-right: 24px;
}

.awardSelect {
  flex: 1 0 411px;
}

.awardWinners {
  @media (max-width: $lg-breakpoint) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    grid-gap: 13px 25px;
  }

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  grid-gap: 56px 0;
  width: 1000px;
  max-width: 100%;
  margin: 40px auto 0 auto;
}

.myAwardsButton {
  display: flex;
  align-items: center;
  width: 180px;
  height: 40px;
  padding: 0 32px 0 36px;

  > div {
    margin-right: 12px;
  }

  p {
    white-space: nowrap;
  }
}

.awardDescriptionContainer {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  width: 935px;
  margin: 40px auto 0 auto;
}

.awardDescriptionContainerMobile {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    padding: 8px 16px;
    margin-top: 16px;
    background: $lightest-gray;
    border-radius: 8px;

    p {
      margin: auto;
      word-break: break-word;
    }
  }

  display: none;
}

.mobileAwardSelector {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  margin-top: 25px;
  overflow-x: scroll;

  > div {
    margin-right: 16px;
  }
}

.awardBadge {
  @media (max-width: $lg-breakpoint) {
    width: 100px;
    max-width: 100px;
    height: 100px;
    max-height: 100px;
    margin: 0 20px 0 0;
    border-radius: 20px;
  }

  width: 140px;
  max-width: 140px;
  height: 140px;
  max-height: 140px;
  margin-right: 20px;
}
</style>
